import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { ordersAPI } from '../services/api';
import toast from 'react-hot-toast';
import { HiOutlineLockClosed, HiCheck } from 'react-icons/hi';

const Checkout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cartItems, cartTotal, discount, coupon, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);

  const [shippingAddress, setShippingAddress] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'Tunisia',
  });

  const [billingAddress, setBillingAddress] = useState({
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'Tunisia',
  });

  const [sameAsShipping, setSameAsShipping] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (cartItems.length === 0) {
      navigate('/cart');
    }
  }, [cartItems, navigate]);

  const subtotal = cartTotal;
  const shipping = subtotal >= 450 ? 0 : 15; // Free shipping over 450 TND
  const total = subtotal - discount + shipping;

  const handleShippingChange = (e) => {
    const { name, value } = e.target;
    setShippingAddress(prev => ({ ...prev, [name]: value }));
  };

  const handleBillingChange = (e) => {
    const { name, value } = e.target;
    setBillingAddress(prev => ({ ...prev, [name]: value }));
  };

  const validateShipping = () => {
    const required = ['firstName', 'lastName', 'email', 'phone', 'address', 'city', 'state', 'zipCode'];
    for (const field of required) {
      if (!shippingAddress[field]) {
        toast.error(t('validation.required'));
        return false;
      }
    }
    return true;
  };

  const handleContinueToPayment = () => {
    if (validateShipping()) {
      setStep(2);
      window.scrollTo(0, 0);
    }
  };

  const handlePlaceOrder = async () => {
    setLoading(true);

    try {
      const orderData = {
        items: cartItems.map(item => ({
          product: item.product._id,
          variant: item.variant,
          quantity: item.quantity,
          price: item.product.variants?.find(v => v.sku === item.variant)?.price || item.product.price,
        })),
        shippingAddress: {
          firstName: shippingAddress.firstName,
          lastName: shippingAddress.lastName,
          address: shippingAddress.address,
          city: shippingAddress.city,
          state: shippingAddress.state,
          zipCode: shippingAddress.zipCode,
          country: shippingAddress.country,
          phone: shippingAddress.phone,
        },
        billingAddress: sameAsShipping ? {
          firstName: shippingAddress.firstName,
          lastName: shippingAddress.lastName,
          address: shippingAddress.address,
          city: shippingAddress.city,
          state: shippingAddress.state,
          zipCode: shippingAddress.zipCode,
          country: shippingAddress.country,
        } : billingAddress,
        paymentMethod,
        notes,
        couponCode: coupon?.code,
      };

      const response = await ordersAPI.create(orderData);
      await clearCart();
      
      toast.success(t('messages.orderPlaced'));
      navigate(`/order-confirmation/${response.data.order._id}`);
    } catch (error) {
      toast.error(error.response?.data?.message || t('messages.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="animate-fade-in bg-primary-50 min-h-screen">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="font-serif text-2xl">
              LUXE
            </Link>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <HiOutlineLockClosed className="w-4 h-4" />
              {t('checkout.secureCheckout')}
            </div>
          </div>
        </div>
      </div>

      {/* Steps */}
      <div className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-center gap-8">
            <div className={`flex items-center gap-2 ${step >= 1 ? 'text-luxury-charcoal' : 'text-gray-400'}`}>
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                step > 1 ? 'bg-green-500 text-white' : step === 1 ? 'bg-luxury-charcoal text-white' : 'bg-gray-200'
              }`}>
                {step > 1 ? <HiCheck className="w-4 h-4" /> : '1'}
              </span>
              <span className="text-sm">{t('checkout.shippingInfo')}</span>
            </div>
            <div className="w-12 h-px bg-gray-200" />
            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-luxury-charcoal' : 'text-gray-400'}`}>
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                step > 2 ? 'bg-green-500 text-white' : step === 2 ? 'bg-luxury-charcoal text-white' : 'bg-gray-200'
              }`}>
                {step > 2 ? <HiCheck className="w-4 h-4" /> : '2'}
              </span>
              <span className="text-sm">{t('checkout.paymentMethod')}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="lg:flex lg:gap-12">
          {/* Main Content */}
          <div className="flex-1">
            <div className="bg-white p-6 lg:p-8">
              {step === 1 && (
                <>
                  <h2 className="font-serif text-2xl mb-6">{t('checkout.shippingInfo')}</h2>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-sm mb-2">{t('checkout.firstName')} *</label>
                      <input
                        type="text"
                        name="firstName"
                        value={shippingAddress.firstName}
                        onChange={handleShippingChange}
                        className="w-full px-4 py-3 border border-gray-200 focus:outline-none focus:border-luxury-gold"
                      />
                    </div>
                    <div>
                      <label className="block text-sm mb-2">{t('checkout.lastName')} *</label>
                      <input
                        type="text"
                        name="lastName"
                        value={shippingAddress.lastName}
                        onChange={handleShippingChange}
                        className="w-full px-4 py-3 border border-gray-200 focus:outline-none focus:border-luxury-gold"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-sm mb-2">{t('checkout.email')} *</label>
                      <input
                        type="email"
                        name="email"
                        value={shippingAddress.email}
                        onChange={handleShippingChange}
                        className="w-full px-4 py-3 border border-gray-200 focus:outline-none focus:border-luxury-gold"
                      />
                    </div>
                    <div>
                      <label className="block text-sm mb-2">{t('checkout.phone')} *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={shippingAddress.phone}
                        onChange={handleShippingChange}
                        className="w-full px-4 py-3 border border-gray-200 focus:outline-none focus:border-luxury-gold"
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm mb-2">{t('checkout.address')} *</label>
                    <input
                      type="text"
                      name="address"
                      value={shippingAddress.address}
                      onChange={handleShippingChange}
                      className="w-full px-4 py-3 border border-gray-200 focus:outline-none focus:border-luxury-gold"
                      placeholder={t('checkout.address')}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-sm mb-2">{t('checkout.city')} *</label>
                      <input
                        type="text"
                        name="city"
                        value={shippingAddress.city}
                        onChange={handleShippingChange}
                        className="w-full px-4 py-3 border border-gray-200 focus:outline-none focus:border-luxury-gold"
                      />
                    </div>
                    <div>
                      <label className="block text-sm mb-2">{t('checkout.state')} *</label>
                      <input
                        type="text"
                        name="state"
                        value={shippingAddress.state}
                        onChange={handleShippingChange}
                        className="w-full px-4 py-3 border border-gray-200 focus:outline-none focus:border-luxury-gold"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-sm mb-2">{t('checkout.zipCode')} *</label>
                      <input
                        type="text"
                        name="zipCode"
                        value={shippingAddress.zipCode}
                        onChange={handleShippingChange}
                        className="w-full px-4 py-3 border border-gray-200 focus:outline-none focus:border-luxury-gold"
                      />
                    </div>
                    <div>
                      <label className="block text-sm mb-2">{t('checkout.country')}</label>
                      <select
                        name="country"
                        value={shippingAddress.country}
                        onChange={handleShippingChange}
                        className="w-full px-4 py-3 border border-gray-200 focus:outline-none focus:border-luxury-gold bg-white"
                      >
                        <option value="United States">United States</option>
                        <option value="Canada">Canada</option>
                        <option value="United Kingdom">United Kingdom</option>
                        <option value="Australia">Australia</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm mb-2">{t('checkout.orderNotes')}</label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-200 focus:outline-none focus:border-luxury-gold resize-none"
                      placeholder={t('checkout.orderNotes')}
                    />
                  </div>

                  <button
                    onClick={handleContinueToPayment}
                    className="w-full bg-luxury-charcoal text-white py-4 text-sm uppercase tracking-widest hover:bg-gray-800 transition-colors"
                  >
                    {t('checkout.continueToPayment')}
                  </button>
                </>
              )}

              {step === 2 && (
                <>
                  <button
                    onClick={() => setStep(1)}
                    className="text-sm text-gray-500 hover:text-luxury-gold mb-6 flex items-center gap-1"
                  >
                    ← {t('checkout.backToShipping')}
                  </button>

                  <h2 className="font-serif text-2xl mb-6">{t('checkout.paymentMethod')}</h2>

                  {/* Payment Options */}
                  <div className="space-y-4 mb-8">
                    <label className="flex items-center gap-4 p-4 border border-gray-200 cursor-pointer hover:border-luxury-gold transition-colors">
                      <input
                        type="radio"
                        name="payment"
                        value="card"
                        checked={paymentMethod === 'card'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="w-5 h-5 text-luxury-gold"
                      />
                      <div className="flex-1">
                        <span className="font-medium">{t('checkout.creditCard')}</span>
                        <p className="text-sm text-gray-500">{t('checkout.paySecurely')}</p>
                      </div>
                      <div className="flex gap-2">
                        <img src="https://upload.wikimedia.org/wikipedia/commons/0/04/Visa.svg" alt="Visa" className="h-8" />
                        <img src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" alt="Mastercard" className="h-8" />
                      </div>
                    </label>

                    <label className="flex items-center gap-4 p-4 border border-gray-200 cursor-pointer hover:border-luxury-gold transition-colors">
                      <input
                        type="radio"
                        name="payment"
                        value="paypal"
                        checked={paymentMethod === 'paypal'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="w-5 h-5 text-luxury-gold"
                      />
                      <div className="flex-1">
                        <span className="font-medium">{t('checkout.paypal')}</span>
                        <p className="text-sm text-gray-500">{t('checkout.payWithPaypal')}</p>
                      </div>
                      <img src="https://upload.wikimedia.org/wikipedia/commons/b/b5/PayPal.svg" alt="PayPal" className="h-8" />
                    </label>

                    <label className="flex items-center gap-4 p-4 border border-gray-200 cursor-pointer hover:border-luxury-gold transition-colors">
                      <input
                        type="radio"
                        name="payment"
                        value="cod"
                        checked={paymentMethod === 'cod'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="w-5 h-5 text-luxury-gold"
                      />
                      <div className="flex-1">
                        <span className="font-medium">{t('checkout.cashOnDelivery')}</span>
                        <p className="text-sm text-gray-500">{t('checkout.payOnReceive')}</p>
                      </div>
                    </label>
                  </div>

                  {/* Billing Address */}
                  <div className="mb-8">
                    <label className="flex items-center gap-2 cursor-pointer mb-4">
                      <input
                        type="checkbox"
                        checked={sameAsShipping}
                        onChange={(e) => setSameAsShipping(e.target.checked)}
                        className="w-5 h-5"
                      />
                      <span>{t('checkout.sameAsShipping')}</span>
                    </label>

                    {!sameAsShipping && (
                      <div className="bg-gray-50 p-4 space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm mb-2">{t('checkout.firstName')}</label>
                            <input
                              type="text"
                              name="firstName"
                              value={billingAddress.firstName}
                              onChange={handleBillingChange}
                              className="w-full px-4 py-3 border border-gray-200 focus:outline-none focus:border-luxury-gold"
                            />
                          </div>
                          <div>
                            <label className="block text-sm mb-2">{t('checkout.lastName')}</label>
                            <input
                              type="text"
                              name="lastName"
                              value={billingAddress.lastName}
                              onChange={handleBillingChange}
                              className="w-full px-4 py-3 border border-gray-200 focus:outline-none focus:border-luxury-gold"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm mb-2">{t('checkout.address')}</label>
                          <input
                            type="text"
                            name="address"
                            value={billingAddress.address}
                            onChange={handleBillingChange}
                            className="w-full px-4 py-3 border border-gray-200 focus:outline-none focus:border-luxury-gold"
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm mb-2">{t('checkout.city')}</label>
                            <input
                              type="text"
                              name="city"
                              value={billingAddress.city}
                              onChange={handleBillingChange}
                              className="w-full px-4 py-3 border border-gray-200 focus:outline-none focus:border-luxury-gold"
                            />
                          </div>
                          <div>
                            <label className="block text-sm mb-2">{t('checkout.state')}</label>
                            <input
                              type="text"
                              name="state"
                              value={billingAddress.state}
                              onChange={handleBillingChange}
                              className="w-full px-4 py-3 border border-gray-200 focus:outline-none focus:border-luxury-gold"
                            />
                          </div>
                          <div>
                            <label className="block text-sm mb-2">{t('checkout.zipCode')}</label>
                            <input
                              type="text"
                              name="zipCode"
                              value={billingAddress.zipCode}
                              onChange={handleBillingChange}
                              className="w-full px-4 py-3 border border-gray-200 focus:outline-none focus:border-luxury-gold"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={handlePlaceOrder}
                    disabled={loading}
                    className="w-full bg-luxury-gold text-white py-4 text-sm uppercase tracking-widest hover:bg-amber-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="spinner-small" />
                        {t('checkout.processing')}
                      </>
                    ) : (
                      `${t('checkout.placeOrder')} - ${total.toFixed(2)} TND`
                    )}
                  </button>

                  <p className="text-xs text-center text-gray-500 mt-4">
                    {t('checkout.termsAgree')}
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:w-96 mt-8 lg:mt-0">
            <div className="bg-white p-6 lg:p-8 sticky top-24">
              <h3 className="font-serif text-xl mb-6">{t('checkout.orderSummary')}</h3>

              {/* Items */}
              <div className="space-y-4 mb-6 max-h-64 overflow-y-auto">
                {cartItems.map((item) => {
                  const product = item.product;
                  const variant = product?.variants?.find(v => v.sku === item.variant);
                  const price = variant?.price || product?.price || 0;

                  return (
                    <div key={item._id} className="flex gap-4">
                      <div className="w-16 h-20 flex-shrink-0 relative">
                        <img
                          src={product?.mainImage || product?.images?.[0]?.url || '/placeholder.jpg'}
                          alt={product?.name}
                          className="w-full h-full object-cover"
                        />
                        <span className="absolute -top-2 -right-2 w-5 h-5 bg-gray-500 text-white text-xs rounded-full flex items-center justify-center">
                          {item.quantity}
                        </span>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-medium line-clamp-1">{product?.name}</h4>
                        <p className="text-xs text-gray-500">{variant?.name}</p>
                        <p className="text-sm mt-1">{(price * item.quantity).toFixed(2)} TND</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-gray-100 pt-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('cart.subtotal')}</span>
                  <span>{subtotal.toFixed(2)} TND</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>{t('cart.discount')} ({coupon?.code})</span>
                    <span>-{discount.toFixed(2)} TND</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('cart.shipping')}</span>
                  <span>{shipping === 0 ? t('cart.free') : `${shipping.toFixed(2)} TND`}</span>
                </div>
                <div className="flex justify-between pt-3 border-t border-gray-100">
                  <span className="font-serif text-lg">{t('cart.orderTotal')}</span>
                  <span className="font-serif text-xl">{total.toFixed(2)} TND</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
